import { useState } from 'react';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Breadcrumbs,
  Chip,
  Divider,
  IconButton,
  Link as MuiLink,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SyncIcon from '@mui/icons-material/Sync';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Link, useLocation } from '@tanstack/react-router';
import { useAuth } from '../../auth/useAuth';
import { useBookStore } from '../../stores/book.store';
import { useNotifications } from '../../api/queries/notifications';

/** Static mapping from route paths to human-readable breadcrumb labels. */
const ROUTE_LABELS: Record<string, string> = {
  '/': 'Dashboard',
  '/books': 'Books',
  '/entities': 'Entities',
  '/worlds': 'Worlds',
  '/images': 'Media Library',
  '/settings': 'Settings',
  '/debug/ai-actions': 'AI Actions',
  '/libraries/shared': 'Shared Library',
};

function useBreadcrumbs(): Array<{ label: string; to?: string }> {
  const location = useLocation();
  const pathname = location.pathname;
  const crumbs: Array<{ label: string; to?: string }> = [];

  // Always start with Dashboard
  crumbs.push({ label: 'Dashboard', to: '/' });

  // If we are at the root, just show Dashboard
  if (pathname === '/') return crumbs;

  const staticLabel = ROUTE_LABELS[pathname];
  if (staticLabel && pathname !== '/') {
    crumbs.push({ label: staticLabel });
  } else if (pathname.startsWith('/books/') && pathname.endsWith('/resume')) {
    crumbs.push({ label: 'Books', to: '/books' });
    crumbs.push({ label: 'Resume' });
  } else if (
    pathname.startsWith('/books/') &&
    pathname.includes('/consistency')
  ) {
    crumbs.push({ label: 'Books', to: '/books' });
    crumbs.push({ label: 'Consistency' });
  } else if (pathname.startsWith('/books/') && pathname.includes('/outline')) {
    crumbs.push({ label: 'Books', to: '/books' });
    crumbs.push({ label: 'Outline' });
  } else if (
    pathname.startsWith('/books/') &&
    pathname.includes('/story-studio')
  ) {
    crumbs.push({ label: 'Books', to: '/books' });
    crumbs.push({ label: 'Story Studio' });
  } else if (pathname.startsWith('/books/') && pathname.includes('/boards')) {
    crumbs.push({ label: 'Books', to: '/books' });
    crumbs.push({ label: 'Boards' });
  } else if (pathname.startsWith('/books/')) {
    crumbs.push({ label: 'Books', to: '/books' });
    crumbs.push({ label: 'Editor' });
  } else if (pathname.startsWith('/entities/')) {
    crumbs.push({ label: 'Entities', to: '/entities' });
    crumbs.push({ label: 'Detail' });
  } else if (pathname.startsWith('/worlds/')) {
    crumbs.push({ label: 'Worlds', to: '/worlds' });
    crumbs.push({ label: 'Builder' });
  }

  return crumbs;
}

export function Topbar() {
  const auth = useAuth();
  const dirty = useBookStore((s) => s.dirty);
  const activeBookId = useBookStore((s) => s.activeBookId);
  const breadcrumbs = useBreadcrumbs();
  const { data: notifications } = useNotifications(true);
  const unreadCount = notifications?.length ?? 0;

  // Notification menu
  const [notifAnchor, setNotifAnchor] = useState<null | HTMLElement>(null);
  const notifOpen = Boolean(notifAnchor);

  // User menu
  const [userAnchor, setUserAnchor] = useState<null | HTMLElement>(null);
  const userOpen = Boolean(userAnchor);

  return (
    <AppBar
      position="static"
      color="transparent"
      elevation={0}
      sx={{ borderBottom: 1, borderColor: 'divider' }}
    >
      <Toolbar
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          minHeight: 56,
        }}
      >
        {/* Left: Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ '& .MuiBreadcrumbs-separator': { mx: 0.5 } }}
        >
          {breadcrumbs.map((crumb, idx) => {
            const isLast = idx === breadcrumbs.length - 1;
            if (isLast || !crumb.to) {
              return (
                <Typography
                  key={idx}
                  variant="body2"
                  color="text.primary"
                  fontWeight={600}
                >
                  {crumb.label}
                </Typography>
              );
            }
            return (
              <MuiLink
                key={idx}
                component={Link}
                to={crumb.to}
                underline="hover"
                variant="body2"
                color="text.secondary"
              >
                {crumb.label}
              </MuiLink>
            );
          })}
        </Breadcrumbs>

        {/* Right side controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Autosave indicator — only show when a book is active */}
          {activeBookId && (
            <Chip
              size="small"
              icon={
                dirty ? (
                  <SyncIcon
                    fontSize="small"
                    sx={{
                      animation: 'spin 1s linear infinite',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }}
                  />
                ) : (
                  <CheckCircleIcon fontSize="small" />
                )
              }
              label={dirty ? 'Saving...' : 'Saved'}
              color={dirty ? 'warning' : 'success'}
              variant="outlined"
              sx={{ mr: 1 }}
            />
          )}

          {/* Notification bell */}
          <IconButton
            size="small"
            onClick={(e) => setNotifAnchor(e.currentTarget)}
            aria-label="Notifications"
          >
            <Badge badgeContent={unreadCount} color="error" max={99}>
              <NotificationsIcon fontSize="small" />
            </Badge>
          </IconButton>
          <Menu
            anchorEl={notifAnchor}
            open={notifOpen}
            onClose={() => setNotifAnchor(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            slotProps={{ paper: { sx: { width: 320, maxHeight: 400 } } }}
          >
            {notifications && notifications.length > 0 ? (
              notifications.slice(0, 10).map((n) => (
                <MenuItem key={n.id} onClick={() => setNotifAnchor(null)}>
                  <ListItemText
                    primary={n.title}
                    secondary={n.message}
                    primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
                    secondaryTypographyProps={{
                      fontSize: 12,
                      noWrap: true,
                    }}
                  />
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                <ListItemText
                  primary="No new notifications"
                  primaryTypographyProps={{
                    fontSize: 13,
                    color: 'text.secondary',
                  }}
                />
              </MenuItem>
            )}
          </Menu>

          {/* User avatar + menu */}
          <IconButton
            size="small"
            onClick={(e) => setUserAnchor(e.currentTarget)}
            aria-label="User menu"
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                fontSize: 14,
                bgcolor: 'primary.main',
              }}
            >
              {auth.email?.charAt(0).toUpperCase() ?? 'U'}
            </Avatar>
          </IconButton>
          {!userOpen && auth.isSignedIn && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ maxWidth: 160, display: { xs: 'none', md: 'block' } }}
            >
              {auth.email}
            </Typography>
          )}
          <Menu
            anchorEl={userAnchor}
            open={userOpen}
            onClose={() => setUserAnchor(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem
              component={Link}
              to="/settings"
              onClick={() => setUserAnchor(null)}
            >
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                setUserAnchor(null);
                auth.signOut();
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Sign Out" />
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
